import { ChangeEvent, FC, ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { FloorMap } from 'src/components/FloorMap/FloorMap';
import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import {
  Point,
  RenderBookingObjectParams,
} from 'src/components/FloorMap/types';
import { getAdminState } from 'src/store/admin/selectors';
import { uploadPlan } from 'src/store/admin/services';
import { adminActions, drawPlace, openPlace } from 'src/store/admin/slice';
import { getAgentState, getIsDesktop } from 'src/store/agent/selectors';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { BookingObject, PlaceType } from 'src/types';

import { AdministratorDeskPlace } from './AdministratorBookingObjects/AdministratorDeskPlace/AdministratorDeskPlace';
import { AdministratorParkingPlace } from './AdministratorBookingObjects/AdministratorParkingPlace/AdministratorParkingPlace';
import { AdministratorRoomPlace } from './AdministratorBookingObjects/AdministratorRoomPlace/AdministratorRoomPlace';

export const renderBookingObject = ({
  bookingObject,
}: RenderBookingObjectParams<BookingObject>): ReactNode => {
  switch (bookingObject.type) {
    case PlaceType.Desk:
      return (
        <AdministratorDeskPlace
          key={bookingObject.mapPlaceId}
          item={bookingObject}
        />
      );
    case PlaceType.Room:
      return (
        <AdministratorRoomPlace
          key={bookingObject.mapPlaceId}
          item={bookingObject}
        />
      );
    case PlaceType.Parking:
      return (
        <AdministratorParkingPlace
          key={bookingObject.mapPlaceId}
          item={bookingObject}
        />
      );
  }
};

export const AdministratorFloorMap: FC = () => {
  const dispatch = useAppDispatch();

  const isDesktop = useSelector(getIsDesktop);

  const { scrollTo } = useFloorMap();
  const {
    loading,
    bookingObjects,
    ghost,
    selectedPlace,
    bookingObjectType,
    backgroundLoadingError,
  } = useSelector(getAdminState);
  const { touch } = useSelector(getAgentState);

  const resultBookingObjects = useMemo(() => {
    const extraBookingObject = selectedPlace ?? ghost;

    if (!extraBookingObject) return bookingObjects;

    const result = new Map(bookingObjects);
    result.set(extraBookingObject.mapPlaceId, extraBookingObject);

    return result;
  }, [bookingObjects, ghost, selectedPlace]);

  const drawGhost = useCallback(
    (point: Point) => {
      dispatch(adminActions.drawGhost(point));
    },
    [dispatch],
  );

  const removeGhost = useCallback(() => {
    dispatch(adminActions.removeGhost());
  }, [dispatch]);

  const draw = useCallback(
    async (point: Point) => {
      if (selectedPlace || !bookingObjectType) return;

      dispatch(drawPlace(point));

      scrollTo(point);
    },
    [bookingObjectType, dispatch, scrollTo, selectedPlace],
  );

  const move = useCallback(
    (point: Point) => {
      dispatch(adminActions.move(point));
    },
    [dispatch],
  );

  const edit = useCallback(
    async (id: string) => {
      const nextBookingObject = resultBookingObjects.get(id);
      if (!nextBookingObject) return;

      dispatch(openPlace(nextBookingObject));

      if (isDesktop) scrollTo(nextBookingObject);
    },
    [dispatch, isDesktop, resultBookingObjects, scrollTo],
  );

  const supportsDragging = useCallback(
    (id: string) => {
      return id === selectedPlace?.mapPlaceId;
    },
    [selectedPlace?.mapPlaceId],
  );

  const upload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      await dispatch(uploadPlan(file));
    },
    [dispatch],
  );

  return (
    <FloorMap
      loading={loading}
      bookingObjects={resultBookingObjects}
      onUpload={upload}
      onClickOnMap={draw}
      onCursorMove={drawGhost}
      onCursorLeave={removeGhost}
      onMoveMap={removeGhost}
      onClickOnBookingObject={edit}
      onDragBookingObject={move}
      renderBookingObject={renderBookingObject}
      showDisabled
      supportsUpload
      supportsAdd
      supportsDragging={supportsDragging}
      touchDevice={touch}
      backgroundLoadingError={backgroundLoadingError}
    />
  );
};
